import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Empty,
  IcnAlert,
  IcnArrowRight,
  IcnBell,
  IcnCheck,
  IcnClose,
  IcnRefresh,
  NotificationCenter,
  Spinner,
  type Notification as NotificationCenterItem,
} from '@viquard/doc-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NOTIFICATION_FILTERS, useNotificationsInbox } from '../hooks/index.js';
import { formatBadgeCount } from '../format.js';
import { NotificationIconAction, NotificationKindIcon } from './notification-display.js';

export interface NotificationsPanelProps {
  onClose?: () => void;
}

export function NotificationsPanel({ onClose }: NotificationsPanelProps): ReactNode {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const inbox = useNotificationsInbox({
    mode: 'append',
    pageSize: 20,
    ...(onClose !== undefined ? { onBeforeNavigate: onClose } : {}),
  });

  const centerItems: NotificationCenterItem[] = inbox.items.map((item) => ({
    id: item.id,
    tone: item.tone,
    title: item.title,
    body: item.aggregateCount > 1 ? `${item.aggregateLabel} - ${item.body}` : item.body,
    time: item.time,
    unread: item.unread,
    leading: <NotificationKindIcon kind={item.kind} />,
    onClick: () => {
      void inbox.open(item);
    },
    actions: (
      <>
        {item.unread ? (
          <NotificationIconAction
            label={t('notifications.markRead')}
            disabled={inbox.isBusy}
            onClick={() => {
              void inbox.markRead(item.id);
            }}
          >
            <IcnCheck size={14} />
          </NotificationIconAction>
        ) : null}
        <NotificationIconAction
          label={t('notifications.dismiss')}
          disabled={inbox.isBusy}
          onClick={() => {
            void inbox.dismiss(item.id);
          }}
        >
          <IcnClose size={14} />
        </NotificationIconAction>
      </>
    ),
  }));

  return (
    <NotificationCenter
      aria-label={t('notifications.label')}
      notifications={centerItems}
      activeTab={inbox.filter}
      onTabChange={(tab) => {
        if (isNotificationFilter(tab)) inbox.setFilter(tab);
      }}
      tabs={NOTIFICATION_FILTERS.map((value) => ({
        id: value,
        label: t(`notifications.filters.${value}`),
      }))}
      header={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate">{t('notifications.title')}</span>
            {inbox.unreadCount > 0 ? (
              <Badge
                tone="danger"
                variant="solid"
                size="sm"
                numeric
                label={t('notifications.unreadLabel', { count: inbox.unreadCount })}
              >
                {formatBadgeCount(inbox.unreadCount)}
              </Badge>
            ) : null}
          </span>
          <Button
            size="sm"
            variant="ghost"
            iconLeft={<IcnCheck size={14} />}
            disabled={inbox.unreadCount === 0}
            onClick={() => {
              void inbox.markAllRead();
            }}
          >
            {t('notifications.markAllRead')}
          </Button>
        </div>
      }
      emptyState={renderEmptyState()}
      footer={
        <div className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="text-xs" style={{ color: 'var(--vq-ink-3)' }}>
            {t('notifications.footer', { shown: inbox.shown, total: inbox.total })}
          </span>
          <span className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              iconLeft={<IcnRefresh size={14} />}
              loading={inbox.isFetching && !inbox.isLoading}
              onClick={inbox.refresh}
            >
              {t('notifications.refresh')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              iconLeft={<IcnArrowRight size={14} />}
              onClick={() => {
                onClose?.();
                void navigate({ to: '/notifications' });
              }}
            >
              {t('notifications.viewAll')}
            </Button>
            {inbox.hasMore ? (
              <Button
                size="sm"
                variant="secondary"
                onClick={inbox.loadMore}
              >
                {t('notifications.loadMore')}
              </Button>
            ) : null}
          </span>
        </div>
      }
    />
  );

  function renderEmptyState(): ReactNode {
    if (inbox.isLoading) {
      return (
        <span className="inline-flex items-center gap-2">
          <Spinner size="sm" />
          {t('notifications.loading')}
        </span>
      );
    }
    if (inbox.error !== null) {
      return (
        <Empty
          size="compact"
          icon={<IcnAlert size={18} />}
          title={t('notifications.loadFailed')}
          action={
            <Button
              size="sm"
              variant="secondary"
              onClick={inbox.refresh}
            >
              {t('notifications.retry')}
            </Button>
          }
        />
      );
    }
    return (
      <Empty
        size="compact"
        icon={<IcnBell size={18} />}
        title={t(`notifications.empty.${inbox.filter}`)}
      />
    );
  }
}

function isNotificationFilter(value: string): value is (typeof NOTIFICATION_FILTERS)[number] {
  return NOTIFICATION_FILTERS.includes(value as (typeof NOTIFICATION_FILTERS)[number]);
}
